import { BRAND } from "@/lib/brand";
import { resolveAssetUrl } from "@/lib/api";

const ATHLETES = [
  {
    id: "felice",
    image: BRAND.FELICE_TESTIMONIAL,
    name: "Felice Falu-Adams",
    role: "#25 · Samoan-American Basketball",
    tags: ["USA", "Samoa National Team", "WCC Pacific"],
    quote:
      '"I was raised to keep God first. I stand by His will every day."',
    scripture: {
      ref: "Philippians 4:13",
      text: '"I can do all things through Christ who strengthens me."',
    },
    accent: "violet",
  },
  {
    id: "jasmine",
    image: BRAND.JASMINE_TESTIMONIAL,
    name: "Jasmine L.",
    role: "Professional Track Athlete",
    tags: ["Albuquerque, NM", "Track & Field", "Faith Driven"],
    quote:
      '"Faith Athletic Gear reminds me who I compete for. Every piece is a statement of my identity in Christ and my commitment to excellence."',
    scripture: {
      ref: "Philippians 4:13",
      text: '"I can do all things through Christ that strengthen me."',
    },
    accent: "amber",
  },
];

const AthleteRow = ({ athlete, reverse }) => {
  const isAmber = athlete.accent === "amber";
  return (
    <div
      className={`grid grid-cols-1 lg:grid-cols-5 gap-12 items-center ${
        reverse ? "lg:[direction:rtl]" : ""
      }`}
      data-testid={`voice-${athlete.id}`}
    >
      <div className="lg:col-span-2 space-y-5 lg:[direction:ltr]">
        <div
          className={`text-[10px] tracking-[0.4em] uppercase ${
            isAmber ? "text-amber-300/80" : "text-violet-300/80"
          }`}
        >
          // Athlete Spotlight
        </div>
        <h3 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight leading-[0.95]">
          {athlete.name.split(" ")[0]} <br />
          <span className="metallic-text">{athlete.name.split(" ").slice(1).join(" ") || "Strong"}</span>
        </h3>
        <blockquote className="space-y-3">
          <p className="text-lg md:text-xl font-heading font-bold italic text-white leading-snug">
            {athlete.quote}
          </p>
          <footer className="space-y-1 pt-2">
            <div className="font-bold text-base tracking-wide uppercase">
              {athlete.name}
            </div>
            <div className="text-xs text-zinc-400 tracking-widest uppercase">
              {athlete.role}
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-1 pt-2 text-[10px] text-zinc-500 tracking-widest uppercase">
              {athlete.tags.map((t, i) => (
                <span key={t} className="flex items-center gap-3">
                  <span>{t}</span>
                  {i < athlete.tags.length - 1 && (
                    <span className="text-zinc-700">·</span>
                  )}
                </span>
              ))}
            </div>
          </footer>
        </blockquote>
        <div
          className={`border-l-4 pl-4 mt-6 ${
            isAmber ? "border-amber-400/60" : "border-violet-400/60"
          }`}
        >
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1">
            {athlete.scripture.ref}
          </div>
          <p className="text-xs text-zinc-300 italic leading-relaxed">
            {athlete.scripture.text}
          </p>
        </div>
      </div>

      <div className="lg:col-span-3 relative lg:[direction:ltr]">
        <div
          className={`absolute -inset-4 blur-2xl rounded-full ${
            isAmber
              ? "bg-gradient-to-br from-amber-500/25 via-transparent to-orange-500/20"
              : "bg-gradient-to-br from-violet-500/20 via-transparent to-amber-500/20"
          }`}
        ></div>
        <img
          src={resolveAssetUrl(athlete.image)}
          alt={`${athlete.name} — Faith Athletic Spotlight`}
          className="relative w-full border border-zinc-800 shadow-2xl"
          data-testid={`voice-${athlete.id}-img`}
        />
      </div>
    </div>
  );
};

const VoicesOfFaith = () => {
  return (
    <section
      id="voices"
      className="relative py-24 border-y border-zinc-900 bg-gradient-to-b from-black via-zinc-950 to-black overflow-hidden"
      data-testid="voices-of-faith-section"
    >
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-violet-500 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-amber-500 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-6xl mx-auto px-6">
        <div className="text-center mb-20">
          <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500 mb-3">
            // The Movement
          </div>
          <h2 className="font-heading text-5xl md:text-7xl font-black uppercase tracking-tight leading-[0.9]">
            Voices of <span className="metallic-text">Faith</span>
          </h2>
          <p className="text-zinc-400 text-sm md:text-base max-w-2xl mx-auto mt-5 leading-relaxed">
            Athletes who compete with conviction. Built in Him. Made to win. These are the stories behind the gear.
          </p>
        </div>

        <div className="space-y-28">
          {ATHLETES.map((a, i) => (
            <AthleteRow key={a.id} athlete={a} reverse={i % 2 === 1} />
          ))}
        </div>

        <div className="text-center mt-24 text-[10px] tracking-[0.4em] uppercase text-amber-300/70">
          Faith · Strength · Purpose &nbsp;|&nbsp; Built In Him. Made To Win.
        </div>
      </div>
    </section>
  );
};

export default VoicesOfFaith;
